import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown, Calendar, DollarSign } from 'lucide-react';
import Icon from '../../../components/AppIcon';




const PerformanceMetrics = ({ data, dateRange }) => {
  const metrics = useMemo(() => {
    if (!data || data?.length === 0) {
      return {
        currentPeriod: { revenue: 0, profit: 0, margin: 0, count: 0 },
        previousPeriod: { revenue: 0, profit: 0, margin: 0, count: 0 },
        growth: { revenue: 0, profit: 0, margin: 0, count: 0 }
      };
    }

    const now = new Date();
    let currentStart, previousStart, previousEnd;

    if (dateRange === 'month') {
      currentStart = new Date(now.getFullYear(), now.getMonth(), 1);
      previousStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      previousEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    } else if (dateRange === 'quarter') {
      const currentQuarter = Math.floor(now?.getMonth() / 3);
      currentStart = new Date(now.getFullYear(), currentQuarter * 3, 1);
      previousStart = new Date(now.getFullYear(), (currentQuarter - 1) * 3, 1);
      previousEnd = new Date(now.getFullYear(), currentQuarter * 3, 0);
    } else {
      currentStart = new Date(now.getFullYear(), 0, 1);
      previousStart = new Date(now.getFullYear() - 1, 0, 1);
      previousEnd = new Date(now.getFullYear() - 1, 11, 31);
    }

    const currentData = data?.filter(calc => {
      const date = new Date(calc?.createdAt);
      return date >= currentStart;
    });

    const previousData = data?.filter(calc => {
      const date = new Date(calc?.createdAt);
      return date >= previousStart && date <= previousEnd;
    });

    const calculatePeriodMetrics = (periodData) => {
      const revenue = periodData?.reduce((sum, calc) => sum + (calc?.totalObra || 0), 0);
      const expenses = periodData?.reduce((sum, calc) => sum + (calc?.totalGastos || 0), 0);
      const profit = revenue - expenses;
      const margin = revenue > 0 ? (profit / revenue) * 100 : 0;

      return {
        revenue,
        profit,
        margin,
        count: periodData?.length
      };
    };

    const current = calculatePeriodMetrics(currentData);
    const previous = calculatePeriodMetrics(previousData);

    const calculateGrowth = (currentVal, previousVal) => {
      if (previousVal === 0) return currentVal > 0 ? 100 : 0;
      return ((currentVal - previousVal) / previousVal) * 100;
    };

    return {
      currentPeriod: current,
      previousPeriod: previous,
      growth: {
        revenue: calculateGrowth(current?.revenue, previous?.revenue),
        profit: calculateGrowth(current?.profit, previous?.profit),
        margin: current?.margin - previous?.margin,
        count: calculateGrowth(current?.count, previous?.count)
      }
    };
  }, [data, dateRange]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const formatGrowth = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value?.toFixed(1)}%`;
  };

  const getGrowthColor = (value) => {
    if (value > 0) return 'text-success';
    if (value < 0) return 'text-destructive';
    return 'text-muted-foreground';
  };

  const getPeriodLabel = () => {
    if (dateRange === 'month') return 'Mês';
    if (dateRange === 'quarter') return 'Trimestre';
    return 'Ano';
  };

  const MetricCard = ({ title, current, previous, growth, icon: Icon, format = 'currency' }) => (
    <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
          </div>
          <h4 className="text-sm font-medium text-muted-foreground">{title}</h4>
        </div>
        <div className="flex items-center gap-1">
          {growth >= 0 ? (
            <TrendingUp className={`h-4 w-4 ${getGrowthColor(growth)}`} />
          ) : (
            <TrendingDown className={`h-4 w-4 ${getGrowthColor(growth)}`} />
          )}
          <span className={`text-xs sm:text-sm font-semibold ${getGrowthColor(growth)}`}>
            {formatGrowth(growth)}
          </span>
        </div>
      </div>
      <div className="space-y-2">
        <div>
          <p className="text-xs text-muted-foreground">{getPeriodLabel()} Atual</p>
          <p className="text-xl sm:text-2xl font-bold text-foreground">
            {format === 'currency' ? formatCurrency(current) : current}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{getPeriodLabel()} Anterior</p>
          <p className="text-sm text-muted-foreground">
            {format === 'currency' ? formatCurrency(previous) : previous}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="h-5 w-5 text-primary" />
        <h3 className="text-lg sm:text-xl font-semibold text-foreground">Comparativo de Desempenho</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
        <MetricCard
          title="Receita"
          current={metrics?.currentPeriod?.revenue}
          previous={metrics?.previousPeriod?.revenue}
          growth={metrics?.growth?.revenue}
          icon={DollarSign}
          format="currency"
        />
        <MetricCard
          title="Lucro Líquido"
          current={metrics?.currentPeriod?.profit}
          previous={metrics?.previousPeriod?.profit}
          growth={metrics?.growth?.profit}
          icon={TrendingUp}
          format="currency"
        />
        <MetricCard
          title="Margem de Lucro"
          current={`${metrics?.currentPeriod?.margin?.toFixed(1)}%`}
          previous={`${metrics?.previousPeriod?.margin?.toFixed(1)}%`}
          growth={metrics?.growth?.margin}
          icon={TrendingUp}
          format="text"
        />
        <MetricCard
          title="Projetos"
          current={metrics?.currentPeriod?.count}
          previous={metrics?.previousPeriod?.count}
          growth={metrics?.growth?.count}
          icon={Calendar}
          format="text"
        />
      </div>
    </div>
  );
};

export default PerformanceMetrics;